/**
 * Email user provisioning utilities
 *
 * Generates fun usernames and passphrases for provisioned email test accounts.
 */

import { readableSlug } from '@chatsdet/types';

const PASSPHRASE_WORDS = [
  'apple', 'arrow', 'beach', 'berry', 'blade', 'blaze', 'bloom', 'board',
  'bones', 'bread', 'brick', 'brush', 'candy', 'cargo', 'chain', 'chalk',
  'chart', 'chess', 'cider', 'cliff', 'clock', 'cloud', 'coral', 'crown',
  'dance', 'delta', 'dream', 'drift', 'ember', 'fence', 'field', 'flame',
  'flash', 'flour', 'forge', 'frost', 'glass', 'globe', 'grape', 'grove',
  'haven', 'honey', 'ivory', 'jewel', 'jelly', 'knack', 'knife', 'lance',
  'laser', 'latch', 'lemon', 'lever', 'light', 'lotus', 'lunar', 'magic',
  'maple', 'marsh', 'medal', 'melon', 'metal', 'mirth', 'mocha', 'motor',
  'noble', 'north', 'ocean', 'olive', 'orbit', 'oxide', 'paper', 'peach',
  'pearl', 'penny', 'piano', 'pilot', 'pixel', 'plank', 'plaza', 'plume',
  'point', 'polar', 'power', 'prism', 'pulse', 'quake', 'quartz', 'radar',
  'realm', 'ridge', 'river', 'robin', 'royal', 'rusty', 'sable', 'satin',
  'scale', 'scout', 'serif', 'shade', 'shark', 'shell', 'shine', 'shore',
  'sigma', 'silk', 'slope', 'solar', 'spark', 'spear', 'spice', 'spine',
  'spoke', 'stamp', 'steam', 'steel', 'stone', 'storm', 'sugar', 'surge',
  'table', 'thorn', 'tiger', 'toast', 'torch', 'tower', 'trail', 'tulip',
  'umbra', 'union', 'vault', 'vigor', 'vivid', 'voice', 'water', 'wheat',
  'yacht', 'yield', 'zebra', 'amber', 'basil', 'cabin', 'cedar', 'coral',
  'daisy', 'elm', 'fable', 'grain', 'hazel', 'index', 'jewel', 'kelp',
  'lilac', 'mango', 'nutmeg', 'oasis', 'pecan', 'quill', 'raven', 'sage',
  'thyme', 'urban', 'vapor', 'waltz', 'xerox', 'youth', 'zephyr', 'alloy',
  'birch', 'clove', 'dusk', 'echo', 'flint', 'glyph', 'heron', 'inlet',
  'jade', 'knoll', 'ledge', 'mist', 'nexus', 'onyx', 'petal', 'quay',
  'reed', 'slate', 'trove', 'umber', 'verge', 'whisk', 'xenon', 'yucca',
  'zinc', 'alder', 'bay', 'crisp', 'dew', 'elk', 'fern', 'gilt',
];

function randomItem<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Extract a short org prefix from the org name.
 * Takes the first 3 consonants (lowercased). Falls back to first 3 alphanumeric chars.
 */
export function getOrgPrefix(orgName: string): string {
  const consonants = orgName
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .split('')
    .filter((c) => !'aeiou'.includes(c));

  if (consonants.length >= 3) {
    return consonants.slice(0, 3).join('');
  }

  // Fallback: first 3 alphanumeric chars
  const alphanumeric = orgName.toLowerCase().replace(/[^a-z0-9]/g, '');
  return alphanumeric.slice(0, 3) || 'usr';
}

/**
 * Generate a fun name in the format: {prefix}-{adjective}-{animal}
 */
export function generateFunName(orgPrefix: string): string {
  return `${orgPrefix}-${readableSlug()}`;
}

/**
 * Generate a passphrase of 4 random words joined by dashes.
 */
export function generatePassphrase(): string {
  const words: string[] = [];
  for (let i = 0; i < 4; i++) {
    words.push(randomItem(PASSPHRASE_WORDS));
  }
  return words.join('-');
}
